import type {
  CoachHealthEvidenceSourceType,
  CoachHealthRecordedContextSourceType,
} from "@/lib/coachHealth/coachHealthEvidenceSourceRegistry";

export const COACH_HEALTH_EVIDENCE_INPUT_VERSION =
  "coach_health_evidence_input_v2" as const;
export const COACH_HEALTH_EVIDENCE_PROFILE_VERSION =
  "coach_health_evidence_profile_v2" as const;

export type CoachHealthPortfolioState = "loading" | "ready" | "error";
export type CoachHealthSourceReadinessState =
  | "loading"
  | "ready"
  | "error"
  | "unavailable";
export type CoachHealthRelationshipLifecycle =
  | "active"
  | "paused"
  | "completed"
  | "archived";
export type CoachHealthRelationshipAccessState =
  | "readable"
  | "grace_period"
  | "hard_gated";

export interface CoachHealthSourceReadinessInput {
  readonly sourceType: CoachHealthEvidenceSourceType;
  readonly state: CoachHealthSourceReadinessState;
}

export interface CoachHealthRelationshipInput {
  readonly coachId: string;
  readonly leaderId: string;
  readonly relationshipRevision: string;
  readonly relationshipStartedAt: string;
  readonly lifecycle: CoachHealthRelationshipLifecycle;
  readonly accessState: CoachHealthRelationshipAccessState;
  readonly authorized: boolean;
  readonly profileResolved: boolean;
}

export interface CoachHealthTranscriptCallLinkLineageInput {
  readonly kind: "transcript_call_link";
  readonly transcriptUploadId: string;
  readonly callId: string;
  readonly coachId: string;
  readonly participantLeaderId: string;
  readonly callStatus: "scheduled" | "completed" | "cancelled";
  readonly callScheduledEnd: string;
}

export interface CoachHealthSessionRecordLineageInput {
  readonly kind: "session_record";
  readonly recordId: string;
  readonly coachId: string;
  readonly leaderId: string;
  readonly recordSourceType: "transcript_review";
  readonly transcriptUploadId: string;
  readonly callLineage: CoachHealthTranscriptCallLinkLineageInput | null;
}

export interface CoachHealthContinuityTranscriptRecordLineageInput {
  readonly recordId: string;
  readonly coachId: string;
  readonly leaderId: string;
  readonly recordSourceType: "transcript_review";
  readonly transcriptUploadId: string;
  readonly transcriptCallLink: CoachHealthTranscriptCallLinkLineageInput | null;
}

export interface CoachHealthContinuityFirstTakeRecordLineageInput {
  readonly recordId: string;
  readonly coachId: string;
  readonly leaderId: string;
  readonly recordSourceType: "coach_first_take";
  readonly payloadVersion: "coach_first_take_v1";
  readonly embeddedCallId: string;
  readonly embeddedCallCoachId: string;
  readonly embeddedParticipantLeaderId: string;
  readonly hasCoachAuthoredResponse: boolean;
  readonly embeddedCallStatus: "scheduled" | "completed" | "cancelled";
  readonly embeddedCallScheduledEnd: string;
}

export type CoachHealthContinuitySourceRecordLineageInput =
  | CoachHealthContinuityTranscriptRecordLineageInput
  | CoachHealthContinuityFirstTakeRecordLineageInput;

export interface CoachHealthPhilosophyEvidenceInput {
  readonly sourceType: "coach_philosophy";
  readonly lensId: string;
  readonly coachId: string;
  readonly coachLensUpdatedAt: string | null;
  readonly updatedAt: string;
  readonly isActive: boolean;
  readonly hasPhilosophy: boolean;
}

interface CoachHealthRelationshipEvidenceBaseInput {
  readonly coachId: string;
  readonly leaderId: string;
  readonly relationshipRevision: string;
}

export interface CoachHealthCompletedCallEvidenceInput
  extends CoachHealthRelationshipEvidenceBaseInput {
  readonly sourceType: "completed_coach_call";
  readonly callId: string;
  readonly scheduledEnd: string;
  readonly status: "scheduled" | "completed" | "cancelled";
  readonly participantLeaderId: string;
}

export interface CoachHealthTranscriptSessionRecordEvidenceInput
  extends CoachHealthRelationshipEvidenceBaseInput {
  readonly sourceType: "coach_transcript_session_record";
  readonly recordId: string;
  readonly updatedAt: string;
  readonly transcriptUploadId: string;
  readonly recordSourceType: "transcript_review";
  readonly callLineage: CoachHealthTranscriptCallLinkLineageInput | null;
}

export interface CoachHealthFirstTakeEvidenceInput
  extends CoachHealthRelationshipEvidenceBaseInput {
  readonly sourceType: "coach_first_take";
  readonly recordId: string;
  readonly updatedAt: string;
  readonly recordSourceType: "coach_first_take";
  readonly payloadVersion: "coach_first_take_v1";
  readonly embeddedCallId: string;
  readonly embeddedCallCoachId: string;
  readonly embeddedParticipantLeaderId: string;
  readonly hasCoachAuthoredResponse: boolean;
  readonly embeddedCallStatus: "scheduled" | "completed" | "cancelled";
  readonly embeddedCallScheduledEnd: string;
}

export interface CoachHealthReviewableTranscriptEvidenceInput
  extends CoachHealthRelationshipEvidenceBaseInput {
  readonly sourceType: "coach_reviewable_transcript";
  readonly transcriptId: string;
  readonly updatedAt: string;
  readonly status: string;
  readonly coachVerified: boolean | null;
  readonly lineage:
    | CoachHealthTranscriptCallLinkLineageInput
    | CoachHealthSessionRecordLineageInput;
}

export interface CoachHealthContinuityMomentEvidenceInput
  extends CoachHealthRelationshipEvidenceBaseInput {
  readonly sourceType: "coaching_continuity_moment";
  readonly momentId: string;
  readonly occurredAt: string;
  readonly kind: "coaching" | "leadership";
  readonly sourceTable: string;
  readonly source: string;
  readonly sourceRecordId: string;
  readonly sourceRecordLineage: CoachHealthContinuitySourceRecordLineageInput;
}

export type CoachHealthEvidenceInput =
  | CoachHealthPhilosophyEvidenceInput
  | CoachHealthCompletedCallEvidenceInput
  | CoachHealthTranscriptSessionRecordEvidenceInput
  | CoachHealthFirstTakeEvidenceInput
  | CoachHealthReviewableTranscriptEvidenceInput
  | CoachHealthContinuityMomentEvidenceInput;

export interface CoachHealthEvidenceAdapterInputV2 {
  readonly version: typeof COACH_HEALTH_EVIDENCE_INPUT_VERSION;
  readonly enabled: boolean;
  readonly authenticatedCoachId: string | null;
  readonly currentScopeKey: string;
  readonly snapshotScopeKey: string;
  readonly asOf: string;
  readonly portfolioState: CoachHealthPortfolioState;
  readonly relationships: readonly CoachHealthRelationshipInput[];
  readonly sourceReadiness: readonly CoachHealthSourceReadinessInput[];
  readonly evidence: readonly CoachHealthEvidenceInput[];
}

export type CoachHealthDimensionName =
  | "philosophy_and_evidence"
  | "support_and_challenge"
  | "continuity"
  | "reflective_adaptation";
export type CoachHealthDimensionState =
  | "evidence_available"
  | "not_yet_available"
  | "insufficient_recorded_context"
  | "partial"
  | "unavailable"
  | "error";

export type CoachHealthExclusionReason =
  | "access_unavailable"
  | "cross_coach"
  | "duplicate_event"
  | "inactive_relationship"
  | "pre_relationship"
  | "relationship_not_eligible"
  | "relationship_revision_mismatch"
  | "source_lineage_invalid"
  | "source_status_ineligible"
  | "source_timestamp_invalid"
  | "unauthorized_relationship"
  | "unresolved_profile";

export interface CoachHealthExclusionCount {
  readonly reason: CoachHealthExclusionReason;
  readonly count: number;
}

export interface CoachHealthEligibleRelationshipKey {
  readonly coachId: string;
  readonly leaderId: string;
}

export interface CoachHealthPublicSourceIssue {
  readonly sourceType: CoachHealthEvidenceSourceType;
  readonly state: "error" | "unavailable";
  readonly publicLabel: string;
  readonly errorCode: string;
}

export interface CoachHealthEvidenceProfileItem {
  readonly sourceType: Exclude<
    CoachHealthEvidenceSourceType,
    "coach_philosophy"
  >;
  readonly sourceId: string;
  readonly evidenceAt: string;
  readonly leaderId: string;
  readonly relationshipRevision: string;
  readonly eventKey: string;
  readonly routeBindingId: string;
  readonly route: string;
}

export type CoachHealthRecordedContextEvidenceItem =
  CoachHealthEvidenceProfileItem & {
    readonly sourceType: CoachHealthRecordedContextSourceType;
  };

export interface CoachHealthRecordedContextCoverage {
  readonly state: CoachHealthDimensionState;
  readonly matchingLeaderIds: readonly string[];
  readonly sourceIssues: readonly CoachHealthPublicSourceIssue[];
  readonly evidence: readonly CoachHealthRecordedContextEvidenceItem[];
}

export interface CoachHealthDimensionResult {
  readonly dimension: CoachHealthDimensionName;
  readonly state: CoachHealthDimensionState;
  readonly matchingLeaderIds: readonly string[];
  readonly sourceIssues: readonly CoachHealthPublicSourceIssue[];
  readonly evidence: readonly CoachHealthEvidenceProfileItem[];
}

interface CoachHealthEvidenceProfileBase {
  readonly version: typeof COACH_HEALTH_EVIDENCE_PROFILE_VERSION;
  readonly asOf: string | null;
  readonly errorCode: string | null;
  readonly eligibleLeaderIds: readonly string[];
  readonly eligibleRelationshipKeys: readonly CoachHealthEligibleRelationshipKey[];
  readonly exclusionCounts: readonly CoachHealthExclusionCount[];
  readonly sourceIssues: readonly CoachHealthPublicSourceIssue[];
}

export interface CoachHealthEvidenceProfileInactive
  extends CoachHealthEvidenceProfileBase {
  readonly state:
    | "idle"
    | "loading"
    | "error"
    | "no_leaders"
    | "no_active_relationships"
    | "access_unavailable";
  readonly dimensions: null;
  readonly recordedContextCoverage: null;
  readonly leaderPerspectiveState: null;
}

export interface CoachHealthEvidenceProfileActive
  extends CoachHealthEvidenceProfileBase {
  readonly state: "ready" | "partial";
  readonly dimensions: readonly CoachHealthDimensionResult[];
  readonly recordedContextCoverage: CoachHealthRecordedContextCoverage;
  readonly leaderPerspectiveState: "unavailable";
}

export type CoachHealthEvidenceProfile =
  | CoachHealthEvidenceProfileInactive
  | CoachHealthEvidenceProfileActive;
